import React, { useEffect, useState } from "react";

// style imports
import "./cookiesBanner.scss";

// plugin imports
import { useLocation } from "@reach/router";
import { initializeAndTrack } from "gatsby-plugin-gdpr-cookies";
import { Trans, useI18next } from "@herob191/gatsby-plugin-react-i18next";
import { AnimatePresence, motion } from "motion/react";
import { Link } from "gatsby";

// import IconoCookies from "./iconoCookies.svg";

// component imports
import ToggleButton from "./toggleButton";
import ClientOnly from "./clientOnly";
import FlashHover from "../../flashHover/flashHover";

// util imports
import { animationStates, fade } from "../../../util/motionAnimations";

// function exports for use in other pages
export const addCookie = (cookieID = "", value = "true", maxAge = null) => {
	const acceptAppendix =
		"=" +
		value +
		";path=/;SameSite=none;Secure;" +
		(maxAge ? "Max-Age=" + maxAge : "");
	if (typeof document != "undefined") {
		document.cookie = cookieID + acceptAppendix;
	}
};

export const addCookies = (date, location) => {
	// console.log("addCookies running");
	const acceptAppendix = "=true;path=/;SameSite=none;Secure";
	// expires=" + date.toGMTString();
	// console.log(acceptAppendix);

	if (typeof document != "undefined") {
		document.cookie = "cookiesStored" + acceptAppendix;
		document.cookie = "cookiesAccepted" + acceptAppendix;
		document.cookie = "gatsby-gdpr-google-analytics" + acceptAppendix;
		document.cookie = "gatsby-gdpr-facebook-pixel" + acceptAppendix;
		document.cookie = "gatsby-gdpr-google-tagmanager" + acceptAppendix;
		document.cookie = "gatsby-gdpr-tiktok-pixel" + acceptAppendix;
		document.cookie = "gatsby-gdpr-linked-in" + acceptAppendix;

		// console.log("Initializing tracking");
		initializeAndTrack(location);
	}
};

export const rejectCookies = (date) => {
	// console.log("rejectCookies running");
	const rejectAppendix =
		"=false;path=/;SameSite=none;Secure;expires=" + date.toGMTString();

	if (typeof document != "undefined") {
		document.cookie = "cookiesStored" + rejectAppendix;

		document.cookie = "cookiesAccepted" + rejectAppendix;
		document.cookie = "gatsby-gdpr-google-analytics" + rejectAppendix;
		document.cookie = "gatsby-gdpr-facebook-pixel" + rejectAppendix;
		document.cookie = "gatsby-gdpr-google-tagmanager" + rejectAppendix;
		document.cookie = "gatsby-gdpr-tiktok-pixel" + rejectAppendix;
		document.cookie = "gatsby-gdpr-linked-in" + rejectAppendix;
	}
};

export const getCookie = (name) => {
	if (typeof document != "undefined") {
		// console.log(document);
		var v = document?.cookie?.match("(^|;) ?" + name + "=([^;]*)(;|$)");
		// console.log(v);
		if (v) {
			if (v[2] === "true") {
				return true;
			} else if (v[2] === "false") {
				return false;
			} else {
				return v[2];
			}
		}
	}
	return null;
};

export const resetCookiesPanel = () => {
	// console.log("rejectCookies running")
	const oneYear = 365 * 24 * 60 * 60 * 1000; //one year in seconds, to set expiry date for the cookie
	const rejectAppendix =
		"=false;path=/;SameSite=none;Secure;Max-Age=" + oneYear;

	if (typeof document != "undefined") {
		document.cookie = "cookiesStored" + rejectAppendix;
	}
};

export default function CookiesBanner({ className = "" }) {
	const { t } = useI18next();

	const [customizeCookies, setCustomizeCookies] = useState(false);
	const [cookiesStored, setCookiesStored] = useState(
		getCookie("cookiesStored"),
	);
	const [funcCookies, setFuncCookies] = useState(true); // eslint-disable-line
	const [mktCookies, setMktCookies] = useState(getCookie("cookiesAccepted"));

	const location = useLocation();

	useEffect(() => {
		// console.log("useEffect running");
		// we have to do this inside the useEffect to prevent Date mismatches from server to client
		const expiry = new Date();
		expiry.setTime(expiry.getTime() + 365 * 24 * 60 * 60 * 1000);

		if (mktCookies && cookiesStored) {
			// if cookies are accepted and have not been previously added, store them
			// console.log("Accepting cookies");
			addCookies(expiry, location);
		}

		if (!mktCookies && cookiesStored) {
			// if cookies are disabled, remove them
			// console.log("Rejecting cookies");s
			rejectCookies(expiry);
		}
	}, [mktCookies, cookiesStored, location]);

	return (
		<ClientOnly id="cookiesContainer" className={className}>
			<AnimatePresence mode="wait">
				{!cookiesStored && (
					<motion.div
						key="cookiesBanner"
						className="cookiesBannerWrapper" // shadow roundedBig
						{...animationStates}
						{...fade}
					>
						<div className="navBarBgs">
							<div className="navBarBlurBg" />
							<div className="navBarNoiseBg" />
						</div>

						<div className="cookiesBannerContainer">
							<span className="h1">
								<Trans>Información de cookies</Trans>
							</span>
							<p className="light">
								<Trans>
									Utilizamos cookies para optimizar tu experiencia en nuestra
									web.
									<br />
									Más info en nuestra{" "}
									<FlashHover>
										<Link to="/legal/cookies" className="underline">
											Política de Cookies
										</Link>
									</FlashHover>
								</Trans>
								.
							</p>
							<AnimatePresence>
								{customizeCookies && (
									<motion.div
										className="customCookies"
										{...animationStates}
										variants={{
											initial: {
												height: 0,
											},
											animate: {
												height: "auto",
											},
											exit: {
												height: 0,
											},
										}}
									>
										<div>
											<div>
												<ToggleButton
													label={t("Técnicas")}
													checked={funcCookies}
													disabled
												/>
												<ToggleButton
													label={t("Marketing")}
													checked={mktCookies}
													onClick={() => {
														setMktCookies(!mktCookies);
													}}
												/>
											</div>
											<div className="saveConfig">
												<FlashHover>
													<button
														onClick={() => {
															setCookiesStored(true);
														}}
														aria-label={t("Guardar configuración")}
													>
														<span className="h2">
															<Trans>Guardar</Trans>
														</span>
													</button>
												</FlashHover>
											</div>
										</div>
									</motion.div>
								)}
							</AnimatePresence>

							<div className="buttons">
								<FlashHover>
									<button
										className="noCookies"
										onClick={() => {
											setCookiesStored(true);
											setMktCookies(false);
										}}
										aria-label={t("Rechazar cookies")}
									>
										<span className="h2">
											<Trans>Rechazar</Trans>
										</span>
									</button>
								</FlashHover>

								<FlashHover>
									<button
										className="noCookies"
										onClick={() => setCustomizeCookies(!customizeCookies)}
										aria-label={t("Personalizar cookies")}
									>
										<span className="h2">
											<Trans>Personalizar</Trans>
										</span>
									</button>
								</FlashHover>

								<FlashHover>
									<button
										onClick={() => {
											setCookiesStored(true);
											setMktCookies(true);
										}}
										aria-label={t("Aceptar cookies")}
									>
										<span className="h1">
											<Trans>Aceptar todas</Trans>
										</span>
									</button>
								</FlashHover>
							</div>
						</div>
					</motion.div>
				)}
			</AnimatePresence>
		</ClientOnly>
	);
}
